import React, { useState, useMemo } from 'react';

import Button from '../../../components/ui/Button';
import { SkeletonBlock, LoadingSpinner } from '../../../components/SkeletonLoader';

const InvoiceTable = ({ invoices, isLoading = false }) => {
  const [sortConfig, setSortConfig] = useState({ key: 'date', direction: 'desc' });
  const [downloadingId, setDownloadingId] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('alle');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [amountMin, setAmountMin] = useState('');
  const [amountMax, setAmountMax] = useState('');
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkExporting, setBulkExporting] = useState(false);

  const getStatusColor = (status) => {
    switch (status) {
      case 'paid': return 'var(--color-success)';
      case 'pending': return 'var(--color-warning)';
      case 'overdue': return 'var(--color-error)';
      default: return 'var(--color-muted-foreground)';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'paid': return 'Betalt';
      case 'pending': return 'Venter';
      case 'overdue': return 'Forfalt';
      default: return 'Ukjent';
    }
  };

  const parseAmount = (amountStr) =>
    parseFloat(amountStr?.replace(/[^\d,]/g, '')?.replace(',', '.')) || 0;

  const parseDate = (dateStr) => {
    const parts = dateStr?.split('/');
    if (parts?.length === 3) return new Date(`${parts[2]}-${parts[1]}-${parts[0]}`);
    return new Date(0);
  };

  const filteredInvoices = useMemo(() => {
    return invoices?.filter((inv) => {
      // Search
      if (searchQuery) {
        const q = searchQuery?.toLowerCase();
        const matchesNumber = inv?.invoiceNumber?.toLowerCase()?.includes(q);
        const matchesDesc = 'premium veiassistent'?.includes(q);
        if (!matchesNumber && !matchesDesc) return false;
      }
      // Status
      if (statusFilter !== 'alle') {
        if (statusFilter === 'betalt' && inv?.status !== 'paid') return false;
        if (statusFilter === 'ubetalt' && inv?.status === 'paid') return false;
      }
      // Date range
      if (dateFrom) {
        const invDate = parseDate(inv?.date);
        if (invDate < new Date(dateFrom)) return false;
      }
      if (dateTo) {
        const invDate = parseDate(inv?.date);
        if (invDate > new Date(dateTo)) return false;
      }
      // Amount range
      const amt = parseAmount(inv?.amount);
      if (amountMin && amt < parseFloat(amountMin)) return false;
      if (amountMax && amt > parseFloat(amountMax)) return false;
      return true;
    });
  }, [invoices, searchQuery, statusFilter, dateFrom, dateTo, amountMin, amountMax]);

  const sortedInvoices = useMemo(() => {
    return [...filteredInvoices]?.sort((a, b) => {
      if (sortConfig?.key === 'date') {
        const dateA = parseDate(a?.date);
        const dateB = parseDate(b?.date);
        return sortConfig?.direction === 'asc' ? dateA - dateB : dateB - dateA;
      }
      if (sortConfig?.key === 'amount') {
        const amountA = parseAmount(a?.amount);
        const amountB = parseAmount(b?.amount);
        return sortConfig?.direction === 'asc' ? amountA - amountB : amountB - amountA;
      }
      return 0;
    });
  }, [filteredInvoices, sortConfig]);

  const handleSort = (key) => {
    setSortConfig({
      key,
      direction: sortConfig?.key === key && sortConfig?.direction === 'asc' ? 'desc' : 'asc'
    });
  };

  const allSelected = sortedInvoices?.length > 0 && sortedInvoices?.every(inv => selectedIds?.includes(inv?.id));

  const toggleSelectAll = () => {
    if (allSelected) {
      setSelectedIds([]);
    } else {
      setSelectedIds(sortedInvoices?.map(inv => inv?.id));
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev =>
      prev?.includes(id) ? prev?.filter(i => i !== id) : [...prev, id]
    );
  };

  const handleBulkExport = () => {
    setBulkExporting(true);
    const toExport = sortedInvoices?.filter(inv => selectedIds?.includes(inv?.id));
    const headers = ['Fakturanummer', 'Dato', 'Beløp', 'Status'];
    const rows = toExport?.map(inv => [
      inv?.invoiceNumber,
      inv?.date,
      inv?.amount,
      getStatusText(inv?.status)
    ]);
    const csvContent = [headers, ...rows]?.map(row => row?.map(cell => `"${cell}"`)?.join(','))?.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `fakturaer-${new Date()?.toISOString()?.slice(0, 10)}.csv`;
    document.body?.appendChild(link);
    link?.click();
    document.body?.removeChild(link);
    URL.revokeObjectURL(url);
    setTimeout(() => setBulkExporting(false), 1000);
  };

  const handleDownload = (invoice) => {
    setDownloadingId(invoice?.id);
    const statusText = getStatusText(invoice?.status);
    const statusColor = invoice?.status === 'paid' ? '#16a34a' : invoice?.status === 'pending' ? '#d97706' : '#dc2626';
    const htmlContent = `<!DOCTYPE html>
<html lang="no">
<head>
  <meta charset="UTF-8" />
  <title>Faktura ${invoice?.invoiceNumber}</title>
  <style>
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body { font-family: 'Segoe UI', Arial, sans-serif; color: #1a1a2e; background: #fff; padding: 48px; }
    .header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 48px; padding-bottom: 24px; border-bottom: 3px solid #004F98; }
    .company-name { font-size: 28px; font-weight: 700; color: #004F98; letter-spacing: -0.5px; }
    .company-sub { font-size: 13px; color: #6b7280; margin-top: 4px; }
    .invoice-badge { background: #004F98; color: #fff; padding: 8px 20px; border-radius: 4px; font-size: 13px; font-weight: 700; letter-spacing: 0.08em; text-transform: uppercase; }
    .invoice-meta { display: grid; grid-template-columns: 1fr 1fr; gap: 32px; margin-bottom: 40px; }
    .meta-block h3 { font-size: 11px; font-weight: 600; color: #9ca3af; text-transform: uppercase; letter-spacing: 0.1em; margin-bottom: 8px; }
    .meta-block p { font-size: 15px; color: #1a1a2e; font-weight: 500; }
    .meta-block .sub { font-size: 13px; color: #6b7280; margin-top: 2px; }
    table { width: 100%; border-collapse: collapse; margin-bottom: 32px; }
    thead tr { background: #f8fafc; border-bottom: 2px solid #e5e7eb; }
    thead th { padding: 12px 16px; text-align: left; font-size: 12px; font-weight: 600; color: #6b7280; text-transform: uppercase; letter-spacing: 0.06em; }
    tbody tr { border-bottom: 1px solid #f3f4f6; }
    tbody td { padding: 14px 16px; font-size: 14px; color: #374151; }
    .amount-col { text-align: right; font-weight: 600; }
    .total-row { background: #f8fafc; }
    .total-row td { font-weight: 700; font-size: 15px; color: #1a1a2e; padding: 16px; }
    .status-badge { display: inline-block; padding: 4px 12px; border-radius: 4px; font-size: 12px; font-weight: 600; border: 2px solid ${statusColor}; color: ${statusColor}; background: ${statusColor}20; }
    .footer { margin-top: 48px; padding-top: 24px; border-top: 1px solid #e5e7eb; display: flex; justify-content: space-between; align-items: center; }
    .footer p { font-size: 12px; color: #9ca3af; }
    .accent-line { width: 48px; height: 3px; background: #FF5722; margin-bottom: 16px; }
  </style>
</head>
<body>
  <div class="header">
    <div>
      <div class="company-name">Veiassistent</div>
      <div class="company-sub">Norges ledende bomkostnadsberegner</div>
    </div>
    <div class="invoice-badge">Faktura</div>
  </div>
  <div class="invoice-meta">
    <div class="meta-block">
      <div class="accent-line"></div>
      <h3>Fakturanummer</h3>
      <p>${invoice?.invoiceNumber}</p>
      <p class="sub">Fakturadato: ${invoice?.date}</p>
    </div>
    <div class="meta-block">
      <div class="accent-line" style="background:#004F98"></div>
      <h3>Status</h3>
      <span class="status-badge">${statusText}</span>
    </div>
  </div>
  <table>
    <thead>
      <tr>
        <th>Beskrivelse</th>
        <th>Periode</th>
        <th>Antall</th>
        <th class="amount-col">Beløp</th>
      </tr>
    </thead>
    <tbody>
      <tr>
        <td>Premium Veiassistent-abonnement</td>
        <td>${invoice?.date}</td>
        <td>1</td>
        <td class="amount-col">${invoice?.amount}</td>
      </tr>
      <tr class="total-row">
        <td colspan="3"><strong>Totalt (inkl. MVA)</strong></td>
        <td class="amount-col">${invoice?.amount}</td>
      </tr>
    </tbody>
  </table>
  <div class="footer">
    <div>
      <p>Veiassistent AS &bull; Org.nr: 123 456 789</p>
      <p>support@veiassistent.no &bull; veiassistent.no</p>
    </div>
    <p>Takk for at du bruker Veiassistent!</p>
  </div>
</body>
</html>`;
    const printWindow = window.open('', '_blank', 'width=900,height=700');
    if (!printWindow) {
      setDownloadingId(null);
      alert('Popup ble blokkert. Tillat popup-vinduer for å laste ned faktura.');
      return;
    }
    printWindow?.document?.write(htmlContent);
    printWindow?.document?.close();
    printWindow.onload = () => {
      setTimeout(() => {
        printWindow?.focus();
        printWindow?.print();
        setDownloadingId(null);
      }, 300);
    };
    setTimeout(() => setDownloadingId(null), 3000);
  };

  const inputStyle = {
    background: 'transparent',
    border: 'none',
    borderBottom: '2px solid var(--color-border)',
    borderRadius: 0,
    padding: '8px 4px',
    fontFamily: 'var(--font-body)',
    fontSize: '13px',
    color: 'var(--color-foreground)',
    outline: 'none',
    width: '100%',
    transition: 'border-color 200ms',
  };

  const labelStyle = {
    fontFamily: 'var(--font-caption)',
    fontSize: '11px',
    fontWeight: 600,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    color: 'var(--color-muted-foreground)',
    marginBottom: '4px',
    display: 'block',
  };

  const hasActiveFilters = searchQuery || statusFilter !== 'alle' || dateFrom || dateTo || amountMin || amountMax;

  const clearFilters = () => {
    setSearchQuery('');
    setStatusFilter('alle');
    setDateFrom('');
    setDateTo('');
    setAmountMin('');
    setAmountMax('');
    setSelectedIds([]);
  };

  if (isLoading) {
    return (
      <div style={{ position: 'relative' }}>
        {/* Filter bar skeleton */}
        <div style={{ marginBottom: '24px', display: 'flex', gap: '12px', alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <SkeletonBlock width="220px" height="38px" style={{ borderRadius: 'var(--radius-sm)' }} />
          <SkeletonBlock width="140px" height="38px" style={{ borderRadius: 'var(--radius-sm)' }} />
        </div>
        {/* Table header skeleton */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '16px',
            padding: '10px 16px',
            borderTop: '2px solid var(--color-border)',
            borderBottom: '2px solid var(--color-border)',
            background: 'var(--color-muted)',
          }}
        >
          <SkeletonBlock width="16px" height="16px" style={{ borderRadius: '3px', flexShrink: 0 }} />
          <SkeletonBlock width="100px" height="13px" />
          <SkeletonBlock width="80px" height="13px" style={{ marginLeft: 'auto' }} />
          <SkeletonBlock width="80px" height="13px" />
          <SkeletonBlock width="60px" height="13px" />
          <SkeletonBlock width="80px" height="13px" />
        </div>
        {/* Row skeletons */}
        {[0, 1, 2, 3, 4]?.map(i => (
          <div
            key={i}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '16px',
              padding: '16px',
              borderBottom: '1px solid var(--color-border)',
              animationDelay: `${i * 0.08}s`,
            }}
          >
            <SkeletonBlock width="16px" height="16px" style={{ borderRadius: '3px', flexShrink: 0 }} />
            <SkeletonBlock width={`${100 + i * 12}px`} height="15px" />
            <SkeletonBlock width="80px" height="15px" style={{ marginLeft: 'auto' }} />
            <SkeletonBlock width={`${60 + i * 8}px`} height="15px" />
            <SkeletonBlock width="64px" height="24px" style={{ borderRadius: 'var(--radius-sm)' }} />
            <SkeletonBlock width="88px" height="34px" style={{ borderRadius: 'var(--radius-sm)' }} />
          </div>
        ))}
        {/* Loading indicator */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '10px',
            padding: '20px',
            borderTop: '1px solid var(--color-border)',
          }}
        >
          <LoadingSpinner size={20} color="#FF5722" />
          <span
            style={{
              fontFamily: 'var(--font-heading)',
              fontSize: '11px',
              fontWeight: 700,
              letterSpacing: '0.1em',
              textTransform: 'uppercase',
              color: 'var(--color-muted-foreground)',
            }}
          >
            Laster fakturaer...
          </span>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Filter & Search Bar */}
      <div style={{ marginBottom: '24px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
        {/* Search + Export row */}
        <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-end', flexWrap: 'wrap' }}>
          {/* Search */}
          <div style={{ flex: '1 1 220px', minWidth: '180px' }}>
            <label style={labelStyle}>Søk faktura</label>
            <div style={{ position: 'relative' }}>
              <input
                type="text"
                placeholder="Fakturanummer eller beskrivelse..."
                value={searchQuery}
                onChange={e => setSearchQuery(e?.target?.value)}
                style={{ ...inputStyle, paddingLeft: '28px' }}
                onFocus={e => e.target.style.borderBottomColor = 'var(--color-accent)'}
                onBlur={e => e.target.style.borderBottomColor = 'var(--color-border)'}
              />
              <svg style={{ position: 'absolute', left: '4px', top: '50%', transform: 'translateY(-50%)', opacity: 0.4 }} width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>
            </div>
          </div>

          {/* Status filter */}
          <div style={{ flex: '0 0 160px' }}>
            <label style={labelStyle}>Status</label>
            <select
              value={statusFilter}
              onChange={e => setStatusFilter(e?.target?.value)}
              style={{ ...inputStyle, cursor: 'pointer', appearance: 'none', paddingRight: '24px', background: 'transparent' }}
              onFocus={e => e.target.style.borderBottomColor = 'var(--color-accent)'}
              onBlur={e => e.target.style.borderBottomColor = 'var(--color-border)'}
            >
              <option value="alle">Alle</option>
              <option value="betalt">Betalt</option>
              <option value="ubetalt">Ubetalt</option>
            </select>
          </div>

          {/* Bulk export */}
          {selectedIds?.length > 0 && (
            <button
              onClick={handleBulkExport}
              disabled={bulkExporting}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '8px 18px',
                background: 'var(--color-accent)',
                border: '2px solid var(--color-accent)',
                borderRadius: 'var(--radius-sm)',
                fontFamily: 'var(--font-heading)',
                fontSize: '12px',
                fontWeight: 700,
                letterSpacing: '0.06em',
                textTransform: 'uppercase',
                color: '#fff',
                cursor: bulkExporting ? 'not-allowed' : 'pointer',
                opacity: bulkExporting ? 0.7 : 1,
                transition: 'transform 150ms, box-shadow 150ms',
                whiteSpace: 'nowrap',
                flexShrink: 0,
              }}
              onMouseEnter={e => { if (!bulkExporting) { e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.boxShadow = '0 6px 16px rgba(255,87,34,0.3)'; }}}
              onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = 'none'; }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
              {bulkExporting ? 'Eksporterer...' : `Eksporter CSV (${selectedIds?.length})`}
            </button>
          )}

          {/* Clear filters */}
          {hasActiveFilters && (
            <button
              onClick={clearFilters}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                padding: '8px 14px',
                background: 'transparent',
                border: '2px solid var(--color-border)',
                borderRadius: 'var(--radius-sm)',
                fontFamily: 'var(--font-caption)',
                fontSize: '12px',
                fontWeight: 600,
                color: 'var(--color-muted-foreground)',
                cursor: 'pointer',
                whiteSpace: 'nowrap',
                flexShrink: 0,
              }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--color-accent)'; e.currentTarget.style.color = 'var(--color-accent)'; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--color-border)'; e.currentTarget.style.color = 'var(--color-muted-foreground)'; }}
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              Nullstill
            </button>
          )}
        </div>

        {/* Date + Amount row */}
        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div style={{ flex: '1 1 140px', minWidth: '120px' }}>
            <label style={labelStyle}>Dato fra</label>
            <input
              type="date"
              value={dateFrom}
              onChange={e => setDateFrom(e?.target?.value)}
              style={inputStyle}
              onFocus={e => e.target.style.borderBottomColor = 'var(--color-accent)'}
              onBlur={e => e.target.style.borderBottomColor = 'var(--color-border)'}
            />
          </div>
          <div style={{ flex: '1 1 140px', minWidth: '120px' }}>
            <label style={labelStyle}>Dato til</label>
            <input
              type="date"
              value={dateTo}
              onChange={e => setDateTo(e?.target?.value)}
              style={inputStyle}
              onFocus={e => e.target.style.borderBottomColor = 'var(--color-accent)'}
              onBlur={e => e.target.style.borderBottomColor = 'var(--color-border)'}
            />
          </div>
          <div style={{ flex: '1 1 120px', minWidth: '100px' }}>
            <label style={labelStyle}>Beløp min (kr)</label>
            <input
              type="number"
              placeholder="0"
              value={amountMin}
              onChange={e => setAmountMin(e?.target?.value)}
              style={inputStyle}
              onFocus={e => e.target.style.borderBottomColor = 'var(--color-accent)'}
              onBlur={e => e.target.style.borderBottomColor = 'var(--color-border)'}
            />
          </div>
          <div style={{ flex: '1 1 120px', minWidth: '100px' }}>
            <label style={labelStyle}>Beløp maks (kr)</label>
            <input
              type="number"
              placeholder="9999"
              value={amountMax}
              onChange={e => setAmountMax(e?.target?.value)}
              style={inputStyle}
              onFocus={e => e.target.style.borderBottomColor = 'var(--color-accent)'}
              onBlur={e => e.target.style.borderBottomColor = 'var(--color-border)'}
            />
          </div>
        </div>

        {/* Results count */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span style={{ fontFamily: 'var(--font-caption)', fontSize: '12px', color: 'var(--color-muted-foreground)' }}>
            {sortedInvoices?.length} faktura{sortedInvoices?.length !== 1 ? 'er' : ''} vist
            {selectedIds?.length > 0 && ` · ${selectedIds?.length} valgt`}
          </span>
        </div>
      </div>
      {sortedInvoices?.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px 24px', color: 'var(--color-muted-foreground)', fontFamily: 'var(--font-caption)', fontSize: '14px' }}>
          Ingen fakturaer matcher søket ditt
        </div>
      ) : (
        <>
          {/* Desktop Table View */}
          <div className="hidden lg:block overflow-x-auto scrollbar-custom">
            <table className="w-full">
              <thead>
                <tr className="border-b-2 border-border">
                  <th className="py-4 px-4 w-10">
                    <input
                      type="checkbox"
                      checked={allSelected}
                      onChange={toggleSelectAll}
                      style={{ accentColor: 'var(--color-accent)', width: '16px', height: '16px', cursor: 'pointer' }}
                      aria-label="Velg alle"
                    />
                  </th>
                  <th className="text-left py-4 px-4 text-sm font-semibold text-foreground">
                    Fakturanummer
                  </th>
                  <th
                    className="text-left py-4 px-4 text-sm font-semibold text-foreground cursor-pointer hover:text-primary transition-colors"
                    onClick={() => handleSort('date')}
                  >
                    <div className="flex items-center gap-2">
                      Dato
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        {sortConfig?.key === 'date' && sortConfig?.direction === 'asc'
                          ? <polyline points="18 15 12 9 6 15" />
                          : <polyline points="6 9 12 15 18 9" />}
                      </svg>
                    </div>
                  </th>
                  <th
                    className="text-left py-4 px-4 text-sm font-semibold text-foreground cursor-pointer hover:text-primary transition-colors"
                    onClick={() => handleSort('amount')}
                  >
                    <div className="flex items-center gap-2">
                      Beløp
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        {sortConfig?.key === 'amount' && sortConfig?.direction === 'asc'
                          ? <polyline points="18 15 12 9 6 15" />
                          : <polyline points="6 9 12 15 18 9" />}
                      </svg>
                    </div>
                  </th>
                  <th className="text-left py-4 px-4 text-sm font-semibold text-foreground">
                    Status
                  </th>
                  <th className="text-right py-4 px-4 text-sm font-semibold text-foreground">
                    Handlinger
                  </th>
                </tr>
              </thead>
              <tbody>
                {sortedInvoices?.map((invoice) => (
                  <tr key={invoice?.id} className="border-b border-border hover:bg-muted transition-colors" style={{ background: selectedIds?.includes(invoice?.id) ? 'rgba(255,87,34,0.04)' : undefined }}>
                    <td className="py-4 px-4">
                      <input
                        type="checkbox"
                        checked={selectedIds?.includes(invoice?.id)}
                        onChange={() => toggleSelect(invoice?.id)}
                        style={{ accentColor: 'var(--color-accent)', width: '16px', height: '16px', cursor: 'pointer' }}
                        aria-label={`Velg faktura ${invoice?.invoiceNumber}`}
                      />
                    </td>
                    <td className="py-4 px-4">
                      <span className="text-sm md:text-base text-foreground font-data">
                        {invoice?.invoiceNumber}
                      </span>
                    </td>
                    <td className="py-4 px-4">
                      <span className="text-sm md:text-base text-foreground">
                        {invoice?.date}
                      </span>
                    </td>
                    <td className="py-4 px-4">
                      <span className="text-sm md:text-base font-medium text-foreground whitespace-nowrap">
                        {invoice?.amount}
                      </span>
                    </td>
                    <td className="py-4 px-4">
                      <div
                        className="inline-flex items-center gap-2 px-3 py-1 rounded border-2"
                        style={{
                          borderColor: getStatusColor(invoice?.status),
                          background: `${getStatusColor(invoice?.status)}15`
                        }}
                      >
                        <span
                          className="text-xs md:text-sm font-medium whitespace-nowrap"
                          style={{ color: getStatusColor(invoice?.status) }}
                        >
                          {getStatusText(invoice?.status)}
                        </span>
                      </div>
                    </td>
                    <td className="py-4 px-4">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          iconName={downloadingId === invoice?.id ? 'Loader' : 'Download'}
                          iconSize={16}
                          onClick={() => handleDownload(invoice)}
                          disabled={downloadingId === invoice?.id}
                          aria-label={`Last ned faktura ${invoice?.invoiceNumber}`}
                        >
                          {downloadingId === invoice?.id ? 'Laster...' : 'Last ned'}
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile Card View */}
          <div className="lg:hidden space-y-4">
            {sortedInvoices?.map((invoice) => (
              <div key={invoice?.id} className="card" style={{ border: selectedIds?.includes(invoice?.id) ? '2px solid var(--color-accent)' : undefined }}>
                <div className="flex items-start justify-between mb-4">
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <input
                      type="checkbox"
                      checked={selectedIds?.includes(invoice?.id)}
                      onChange={() => toggleSelect(invoice?.id)}
                      style={{ accentColor: 'var(--color-accent)', width: '16px', height: '16px', cursor: 'pointer', flexShrink: 0 }}
                    />
                    <div>
                      <p className="text-xs text-muted-foreground font-caption mb-1">Fakturanummer</p>
                      <p className="text-sm font-medium text-foreground font-data">{invoice?.invoiceNumber}</p>
                    </div>
                  </div>
                  <div
                    className="inline-flex items-center gap-2 px-3 py-1 rounded border-2"
                    style={{
                      borderColor: getStatusColor(invoice?.status),
                      background: `${getStatusColor(invoice?.status)}15`
                    }}
                  >
                    <span className="text-xs font-medium whitespace-nowrap" style={{ color: getStatusColor(invoice?.status) }}>
                      {getStatusText(invoice?.status)}
                    </span>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div>
                    <p className="text-xs text-muted-foreground font-caption mb-1">Dato</p>
                    <p className="text-sm text-foreground">{invoice?.date}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground font-caption mb-1">Beløp</p>
                    <p className="text-sm font-medium text-foreground whitespace-nowrap">{invoice?.amount}</p>
                  </div>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  iconName={downloadingId === invoice?.id ? 'Loader' : 'Download'}
                  iconPosition="left"
                  iconSize={16}
                  fullWidth
                  disabled={downloadingId === invoice?.id}
                  onClick={() => handleDownload(invoice)}
                >
                  {downloadingId === invoice?.id ? 'Laster ned...' : 'Last ned faktura'}
                </Button>
              </div>
            ))}
          </div>
        </>
      )}
    </>
  );
};

export default InvoiceTable;